import { useState, useEffect, useCallback, useRef } from "react";
import authApi from "../api/auth";
import { clearTokens, getSavedUser, saveUser } from "../api/client";

/**
 * Flatten nested teacher/student profile fields onto the user object
 * so the rest of the app can use user.assignedClasses, user.subject, etc.
 */
function normalizeUser(u) {
  if (!u) return null;
  const flat = { ...u };

  // v2 backend returns employee; v1 returned teacher
  const profile = u.employee || u.teacher;
  if (profile && typeof profile === "object") {
    const { _id: teacherDocId, teaching, lastCheckIn, ...rest } = profile;
    Object.assign(flat, rest);
    if (teaching && typeof teaching === "object") {
      if (teaching.assignedClasses?.length)
        flat.assignedClasses = teaching.assignedClasses;
      if (teaching.classTeacherOf) flat.classTeacherOf = teaching.classTeacherOf;
      if (teaching.subjects?.length) flat.subject = teaching.subjects[0];
    }
    flat.teacherDocId = teacherDocId;
    // Preserve lastCheckIn for attendance display
    if (lastCheckIn) flat.lastCheckIn = lastCheckIn;
    delete flat.employee;
    delete flat.teacher;
  }

  // Fallback display name from email if no name field
  if (!flat.name && flat.email) {
    flat.name = flat.email.split("@")[0];
  }

  flat.id = flat._id || flat.id;
  saveUser(flat);
  return flat;
}

/**
 * Manages the current user session.
 * Uses the real backend API for login/logout.
 * Falls back to seed data matching when the backend is unreachable.
 */
export function useAuth() {
  const [user, setUser] = useState(() => normalizeUser(getSavedUser()));
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // authReady = false while we're verifying an existing token on mount
  const [authReady, setAuthReady] = useState(() => !localStorage.getItem("edupulse_token"));

  // Prevent multiple simultaneous auth checks
  const authCheckRef = useRef(false);

  // Always rehydrate full profile from /auth/me on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem("edupulse_token");
    if (token && !authCheckRef.current) {
      authCheckRef.current = true;
      authApi.me()
        .then(u => {
          const normalized = normalizeUser(u);
          setRequiresSetup(normalized.requiresSetup || false);
          setUser(normalized);
        })
        .catch(() => {
          // Token invalid/expired — clear everything
          clearTokens();
          setUser(null);
        })
        .finally(() => {
          authCheckRef.current = false;
          setAuthReady(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for forced logout (from 401 interceptor)
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener("edupulse:logout", handler);
    return () => window.removeEventListener("edupulse:logout", handler);
  }, []);

  const login = useCallback(async (emailOrAccount, password) => {
    setError(null);
    setLoading(true);

    // If called with an account object (demo buttons) — use its id/pwd
    const email = typeof emailOrAccount === "object" ? emailOrAccount.id : emailOrAccount;
    const pwd = typeof emailOrAccount === "object" ? emailOrAccount.pwd : password;

    try {
      const data = await authApi.login(email, pwd);
      let normalized = normalizeUser(data.user);
      
      // Only fetch full profile if login response lacks role-specific data
      if (!normalized.teacherDocId && !normalized.assignedClasses && normalized.role === 'teacher') {
        try {
          const fullProfile = await authApi.me();
          normalized = normalizeUser(fullProfile);
        } catch { /* use login data as fallback */ }
      }
      
      setUser(normalized);
      setLoading(false);
      return normalized;
    } catch (err) {
      const msg = err.response?.data?.error || "Login failed";
      setError(msg);
      setLoading(false);
      throw new Error(msg);
    }
  }, []);

  // OTP-based login flow
  const sendOTP = useCallback(async (email) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authApi.sendOTP(email);
      setLoading(false);
      return data;
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to send OTP";
      setError(msg);
      setLoading(false);
      throw new Error(msg);
    }
  }, []);

  const verifyOTP = useCallback(async (email, otp) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authApi.verifyOTP(email, otp);
      let normalized = normalizeUser(data.user);
      
      // Only fetch full profile if login response lacks role-specific data
      if (!normalized.teacherDocId && !normalized.assignedClasses && normalized.role === 'teacher') {
        try {
          const fullProfile = await authApi.me();
          normalized = normalizeUser(fullProfile);
        } catch { /* use login data as fallback */ }
      }
      
      // Check if school requires setup
      setRequiresSetup(normalized.requiresSetup || false);
      
      setUser(normalized);
      setLoading(false);
      return normalized;
    } catch (err) {
      const msg = err.response?.data?.error || "OTP verification failed";
      setError(msg);
      setLoading(false);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // clear regardless
    }
    setUser(null);
  }, []);

  const patchUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return { user, login, logout, patchUser, loading, error, sendOTP, verifyOTP, requiresSetup, authReady };
}
